import { action, makeObservable, observable } from "mobx";

/**
 * A MobX-observable map of boolean values, where only one key can be true at a time.
 * The key is generated from the provided arguments using the abstract `mapKey` method.
 * Only changed values are updated in the map to minimize observable updates.
 */
export abstract class ObservedBoolMap<Args extends unknown[]> {
  protected map = new Map<string, boolean>();

  private currentKey: string | null = null;

  constructor() {
    makeObservable<typeof this, "map" | "currentKey">(this, {
      map: observable,
      set: action,
      reset: action,
      clear: action,
      currentKey: observable,
    });
  }

  protected abstract mapKey(...args: Args): string;

  set(...args: Args) {
    const key = this.mapKey(...args);
    if (this.currentKey === key) {
      return;
    }
    if (this.currentKey) {
      this.map.set(this.currentKey, false);
    }
    this.map.set(key, true);
    this.currentKey = key;
  }

  get(...args: Args) {
    const key = this.mapKey(...args);
    return this.map.get(key) || false;
  }

  get current() {
    return this.currentKey;
  }

  reset() {
    if (this.currentKey) {
      this.map.set(this.currentKey, false);
    }
    this.currentKey = null;
  }

  clear() {
    this.map.clear();
    this.currentKey = null;
  }
}
